import {
  Check,
  Copy,
  EllipsisVertical,
  Gift,
  Loader2,
  RefreshCw,
  Upload,
  XCircle,
} from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { toast } from "sonner"
import { Avatar } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { useAuthStore } from "@/features/auth/store/authStore"
import { useTranslation } from "@/language"
import {
  type BulkInviteError,
  cancelInvite,
  getReferralHistory,
  getReferralStats,
  resendInvite,
  sendBulkInvites,
} from "../services/invite.service"
import type {
  ReferralHistoryItem,
  ReferralHistoryResponse,
  ReferralStats,
} from "../types/settings.types"

type HistoryFilter = "all" | "joined" | "pending" | "expired"

const MAX_INVITES_PER_BATCH = 5

function formatDate(dateStr: string | null): string {
  if (!dateStr) return ""
  const date = new Date(dateStr)
  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" })
}

const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email.trim())
}

export function InviteFriendsSection() {
  const { t } = useTranslation("settings")
  const { user } = useAuthStore()

  const [referralCode, setReferralCode] = useState("")
  const [referralUrl, setReferralUrl] = useState("")
  const [shareUrl, setShareUrl] = useState("")
  const [stats, setStats] = useState<ReferralStats>({
    invitesSent: 0,
    pendingInvites: 0,
    joinedByCode: 0,
    joinedViaEmail: 0,
  })
  const [historyFilter, setHistoryFilter] = useState<HistoryFilter>("all")
  const [historyItems, setHistoryItems] = useState<ReferralHistoryItem[]>([])
  const [historyCounts, setHistoryCounts] = useState({
    all: 0,
    joined: 0,
    joinedByCode: 0,
    joinedViaEmail: 0,
    pending: 0,
    expired: 0,
  })
  const [emailInput, setEmailInput] = useState("")
  const [emailError, setEmailError] = useState<string | null>(null)
  const [copied, setCopied] = useState(false)
  const [loading, setLoading] = useState(true)
  const [sendingInvites, setSendingInvites] = useState(false)
  const [actionLoading, setActionLoading] = useState<string | null>(null)

  const goalCount = 10
  const joinedTotal = stats.joinedByCode + stats.joinedViaEmail
  const progressPercent = Math.min((joinedTotal / goalCount) * 100, 100)

  useEffect(() => {
    const username = user?.username || ""
    const appUrl = import.meta.env.VITE_APP_REFERRAL_URL || ""
    const displayUrl = username ? `${appUrl}/${username}` : ""
    const fullShareUrl = username ? `http://${appUrl}/${username}` : ""

    setReferralCode(username)
    setReferralUrl(displayUrl)
    setShareUrl(fullShareUrl)
  }, [user?.username])

  const loadStatsAndHistory = useCallback(async () => {
    try {
      const [statsData, historyData] = (await Promise.race([
        Promise.all([getReferralStats(), getReferralHistory("all")]),
        new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Timeout waiting for referral data")), 10000),
        ),
      ])) as [ReferralStats, ReferralHistoryResponse]
      setStats(statsData)
      setHistoryItems(historyData.invites)
      setHistoryCounts(historyData.counts)
    } catch (error) {
      console.error("Failed to load referral data:", error)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    loadStatsAndHistory()
  }, [loadStatsAndHistory])

  const loadHistory = useCallback(async (filter: HistoryFilter) => {
    try {
      const result = await getReferralHistory(filter)
      setHistoryItems(result.invites)
      setHistoryCounts(result.counts)
    } catch {
      console.error("Failed to load history")
    }
  }, [])

  const handleFilterChange = (filter: HistoryFilter) => {
    setHistoryFilter(filter)
    loadHistory(filter)
  }

  const handleCopyLink = async () => {
    await navigator.clipboard.writeText(referralUrl)
    setCopied(true)
    toast.success(t("inviteFriends.copiedToClipboard"))
    setTimeout(() => setCopied(false), 2000)
  }

  const handleShare = async () => {
    if (!shareUrl) {
      toast.error(t("inviteFriends.referralLinkError"))
      return
    }

    if (navigator.share) {
      try {
        await navigator.share({
          title: t("inviteFriends.shareTitle"),
          text: t("inviteFriends.shareText"),
          url: shareUrl,
        })
      } catch (error) {
        if ((error as Error).name !== "AbortError") {
          handleCopyLink()
        }
      }
    } else {
      handleCopyLink()
    }
  }

  const handleSendInvites = async () => {
    const rawEmails = emailInput
      .split(",")
      .map(e => e.trim())
      .filter(e => e.length > 0)

    if (rawEmails.length === 0) return

    const invalidEmails = rawEmails.filter(email => !isValidEmail(email))
    if (invalidEmails.length > 0) {
      setEmailError(t("inviteFriends.invalidEmails", { emails: invalidEmails.join(", ") }))
      return
    }

    if (rawEmails.length > MAX_INVITES_PER_BATCH) {
      setEmailError(t("inviteFriends.maxEmails", { max: MAX_INVITES_PER_BATCH }))
      return
    }

    setSendingInvites(true)
    try {
      const result = await sendBulkInvites(rawEmails)
      if (result.sent > 0 && result.failed.length === 0) {
        toast.success(t("inviteFriends.inviteSentSuccess"))
      } else if (result.sent > 0 && result.failed.length > 0) {
        toast.error(result.message)
      } else {
        toast.error(result.message)
      }
      setEmailInput("")
      setEmailError(null)
      loadStatsAndHistory()
    } catch (error) {
      const err = error as BulkInviteError
      if (err.message) {
        toast.error(err.message)
      } else if (err.failed && err.failed.length > 0) {
        toast.error(
          t("inviteFriends.failedToSendTo", { emails: err.failed.map(f => f.email).join(", ") }),
        )
      } else {
        toast.error(t("inviteFriends.inviteFailed"))
      }
    } finally {
      setSendingInvites(false)
    }
  }

  const handleResend = async (inviteId: string) => {
    setActionLoading(inviteId)
    try {
      await resendInvite(inviteId)
      loadHistory(historyFilter)
    } catch {
      console.error("Failed to resend invite")
    } finally {
      setActionLoading(null)
    }
  }

  const handleCancel = async (inviteId: string) => {
    setActionLoading(inviteId)
    try {
      await cancelInvite(inviteId)
      loadHistory(historyFilter)
      loadStatsAndHistory()
    } catch {
      console.error("Failed to cancel invite")
    } finally {
      setActionLoading(null)
    }
  }

  const emailCount = emailInput
    .split(",")
    .map(e => e.trim())
    .filter(e => e.length > 0).length

  const invalidEmails = emailInput
    .split(",")
    .map(e => e.trim())
    .filter(e => e.length > 0)
    .filter(e => !isValidEmail(e))

  const hasInvalidEmails = invalidEmails.length > 0

  const handleEmailInputChange = (value: string) => {
    setEmailInput(value)
    if (emailError || value === "") {
      const newEmails = value
        .split(",")
        .map(e => e.trim())
        .filter(e => e.length > 0)
      const newInvalidEmails = newEmails.filter(e => !isValidEmail(e))
      const newExceedsLimit = newEmails.length > MAX_INVITES_PER_BATCH

      if (newInvalidEmails.length > 0) {
        setEmailError(t("inviteFriends.invalidEmails", { emails: newInvalidEmails.join(", ") }))
      } else if (newExceedsLimit) {
        setEmailError(t("inviteFriends.maxEmails", { max: MAX_INVITES_PER_BATCH }))
      } else {
        setEmailError(null)
      }
    }
  }

  const canSendInvites = emailCount > 0 && emailCount <= MAX_INVITES_PER_BATCH && !hasInvalidEmails

  const getStatusBadge = (item: ReferralHistoryItem) => {
    switch (item.status) {
      case "JOINED":
        return (
          <Badge className="bg-brand-olive text-white border-brand-olive text-[13px] rounded-sm">
            {t("inviteFriends.statusJoined")}
          </Badge>
        )
      case "INVITED":
      case "PENDING":
        return (
          <Badge className="bg-[#C9A876] text-[#000000] border-[#C9A876] text-[13px] rounded-sm">
            {t("inviteFriends.statusPending")}
          </Badge>
        )
      case "CANCELLED":
        return (
          <Badge className="bg-brand-terracotta text-[#000000] border-brand-terracotta text-[13px] rounded-sm">
            {t("inviteFriends.statusExpired")}
          </Badge>
        )
    }
  }

  const getStatusText = (item: ReferralHistoryItem) => {
    const date = formatDate(item.joinedAt || item.invitedAt)
    switch (item.status) {
      case "JOINED":
        if (item.joinMethod === "REFERRED_CODE") {
          return t("inviteFriends.joinedUsingCode", { date })
        }
        return t("inviteFriends.joinedViaYourLink", { date })
      case "INVITED":
      case "PENDING":
        return t("inviteFriends.invitePendingSince", { date })
      case "CANCELLED":
        return t("inviteFriends.inviteExpiredText", { date })
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="size-8 animate-spin text-stone" />
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-5.5 w-full">
      <div>
        <h1 className="text-lg md:text-xl font-semibold text-primary leading-5 uppercase">
          {t("inviteFriends.title")}
        </h1>
        <p className="text-sm text-primary mt-2.5 md:mt-1.25">{t("inviteFriends.description")}</p>
      </div>

      <div className="flex flex-col gap-7.5">
        {/* Invite & Earn Card */}
        <div className="flex flex-col xl:flex-row gap-2.5 justify-between rounded-[20px] p-5 bg-[#FFFFFF0D] border border-[#FFFFFF1A]">
          <div className="flex flex-col items-start gap-2.5">
            <div className="size-6 rounded-full flex items-center gap-2.5 shrink-0">
              <span>
                <Gift className="size-6 text-primary" />
              </span>
              <span className="text-[15px] font-semibold leading-6.25 text-primary text-nowrap">
                {t("inviteFriends.inviteAndEarn")}
              </span>
            </div>
            <div className="flex-1">
              <p className="text-sm text-primary leading-6.25">
                {t("inviteFriends.inviteDescription")}
              </p>
            </div>
          </div>
          <div className="flex flex-col gap-2.5 xl:w-[400px]">
            <div className="flex justify-between text-[13px] leading-6.25">
              <span className="text-stone">
                {joinedTotal} / {goalCount}
              </span>
              <span className="text-stone">
                {Math.round(progressPercent)}% {t("inviteFriends.completed")}
              </span>
            </div>
            <div className="h-2 bg-card rounded-md overflow-hidden">
              <div
                className="h-full bg-charcoal rounded-md transition-all"
                style={{ width: `${progressPercent}%` }}
              />
            </div>
          </div>
        </div>

        {/* Your Referral Code */}
        <div className="flex flex-col gap-5 rounded-[20px] p-5 bg-[#FFFFFF0D] border border-[#FFFFFF1A]">
          <div className="flex flex-col gap-2.5">
            <h2 className="text-[15px] leading-6.25 font-semibold text-primary">
              {t("inviteFriends.yourReferralCode")}
            </h2>
            <div className="flex flex-col-reverse md:flex-row md:items-center gap-5">
              <div className="flex items-center gap-3.75 rounded-[10px] p-5 bg-[#FFFFFF0D] border border-[#FFFFFF1A] w-full md:w-auto">
                <span className="text-[17px] font-semibold leading-5 text-white uppercase w-full">
                  {referralCode}
                </span>
                <button
                  onClick={handleCopyLink}
                  className="text-stone hover:text-white transition-colors"
                >
                  {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
                </button>
              </div>
              <span className="text-sm text-[#F2F0EB80]">{t("inviteFriends.letOthersUse")}</span>
            </div>
          </div>
          <p className="text-sm text-primary">{t("inviteFriends.sendYourLink")}</p>
          <div className="flex flex-col gap-5 md:flex-row lg:flex-col xl:flex-row md:items-center md:gap-2.5">
            <div className="flex items-center gap-3.75 rounded-[10px] p-5 bg-[#FFFFFF0D] border border-[#FFFFFF1A] w-full">
              <a
                href={referralUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-[17px] font-semibold leading-5 text-primary truncate w-full"
              >
                {referralUrl}
              </a>
              <button
                onClick={handleCopyLink}
                className="text-stone hover:text-white transition-colors"
              >
                {copied ? <Check className="size-4" /> : <Copy className="size-4" />}
              </button>
            </div>
            <Button
              variant="primary"
              size="md"
              onClick={handleShare}
              className="gap-2 py-4 h-12.5 w-full md:w-[140px] lg:w-full xl:w-[140px] flex-1 md:flex-none"
            >
              <Upload className="size-4" />
              {t("inviteFriends.share")}
            </Button>
          </div>
        </div>

        {/* Invite New User */}
        <div className="flex flex-col gap-3">
          <h2 className="text-[18px] leading-6.25 font-semibold text-primary">
            {t("inviteFriends.inviteNewUser")}
          </h2>
          <div className="flex flex-col md:flex-row items-center gap-5">
            <div className="flex items-center gap-2 rounded-[10px] p-5 bg-[#FFFFFF0D] border border-[#FFFFFF1A] flex-1 w-full">
              <input
                type="text"
                placeholder={t("inviteFriends.enterEmailsPlaceholder")}
                value={emailInput}
                onChange={e => handleEmailInputChange(e.target.value)}
                className="bg-transparent text-sm text-charcoal placeholder:text-stone outline-none flex-1"
              />
              <span className="text-sm text-stone shrink-0">
                {t("inviteFriends.inviteCount", { count: emailCount })}
              </span>
            </div>
            <Button
              variant="primary"
              size="lg"
              onClick={handleSendInvites}
              disabled={sendingInvites || !canSendInvites}
              className="shrink-0 flex-1 w-full md:flex-none md:w-[205px] h-12.5 py-3.75 text-[17px] cursor-pointer"
            >
              {sendingInvites ? (
                <span className="flex gap-2">
                  {t("inviteFriends.processing")} <Loader2 className="size-4 animate-spin" />
                </span>
              ) : (
                t("inviteFriends.sendInvite")
              )}
            </Button>
          </div>
          {emailError && <p className="text-sm text-red-500 mt-1">{emailError}</p>}
        </div>

        {/* Referral Stats */}
        <div className="flex flex-col gap-5">
          <h2 className="text-[18px] font-semibold leading-6.25 text-primary">
            {t("inviteFriends.referralActivity")}
          </h2>
          <div className="grid grid-cols-2 xl:grid-cols-4 gap-3">
            {[
              { label: t("inviteFriends.invitesSent"), value: stats.invitesSent },
              { label: t("inviteFriends.pendingInvites"), value: stats.pendingInvites },
              { label: t("inviteFriends.joinedByCode"), value: stats.joinedByCode },
              { label: t("inviteFriends.joinedViaEmail"), value: stats.joinedViaEmail },
            ].map(stat => (
              <div
                key={stat.label}
                className="flex flex-col gap-1.25 rounded-[20px] p-5 bg-[#FFFFFF0D] border border-[#FFFFFF1A]"
              >
                <span className="text-xl leading-5 font-semibold text-primary">{stat.value}</span>
                <span className="text-sm text-[#F2F0EB80]">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Ritual History */}
        <div className="flex flex-col gap-3">
          <div className="flex flex-col md:flex-row md:items-center gap-2">
            <h2 className="text-[18px] font-semibold text-primary">
              {t("inviteFriends.ritualHistory")}
            </h2>
            <div className="hidden md:block h-[30px] w-px bg-card" />
            <div className="flex items-center">
              {(["all", "joined", "pending", "expired"] as HistoryFilter[]).map(filter => (
                <Button
                  key={filter}
                  variant={"ghost"}
                  size="sm"
                  className={`px-3 ${historyFilter === filter ? "text-primary" : "text-body"}`}
                  onClick={() => handleFilterChange(filter)}
                >
                  {t(`inviteFriends.${filter}`, { count: historyCounts[filter] })}
                </Button>
              ))}
            </div>
          </div>

          <div className="flex flex-col mb-5">
            {historyItems.length === 0 ? (
              <p className="text-sm text-stone text-center py-8">{t("inviteFriends.noInvites")}</p>
            ) : (
              historyItems.map(item => {
                const user = item.joinedUser
                const displayName = user?.name || item.invitedEmail
                const username = user?.username

                return (
                  <div
                    key={item.id}
                    className="flex md:items-center md:flex-row flex-col gap-2 justify-between py-3 border-b border-[rgba(255,255,255,0.05)] last:border-0"
                  >
                    <div className="flex items-center gap-3">
                      <Avatar
                        fallback={displayName.charAt(0).toUpperCase()}
                        src={user?.profileImage ?? undefined}
                        size="lg"
                      />
                      <div className="flex flex-col">
                        <span className="text-sm font-semibold text-charcoal">{displayName}</span>
                        <span className="text-[13px] text-stone">
                          {username
                            ? `@${username}`
                            : item.joinMethod === "REFERRED_CODE"
                              ? t("inviteFriends.joinedViaCode")
                              : t("inviteFriends.invitedViaEmail")}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center justify-end gap-3">
                      <span className="text-[13px] text-stone">{getStatusText(item)}</span>
                      {user && (
                        <span className="text-[13px] text-stone">
                          {t("inviteFriends.followers", { count: user.followerCount })}
                        </span>
                      )}
                      {getStatusBadge(item)}
                      {(item.status === "INVITED" || item.status === "PENDING") && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <button
                              type="button"
                              className="p-1 text-ash hover:text-stone transition-colors cursor-pointer"
                              disabled={actionLoading === item.id}
                            >
                              {actionLoading === item.id ? (
                                <Loader2 className="size-4 animate-spin" />
                              ) : (
                                <EllipsisVertical className="size-4" />
                              )}
                            </button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleResend(item.id)}>
                              <RefreshCw className="size-4 mr-2" />
                              {t("inviteFriends.resendInvite")}
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              onClick={() => handleCancel(item.id)}
                              className="text-red-600"
                            >
                              <XCircle className="size-4 mr-2" />
                              {t("inviteFriends.cancelInvite")}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </div>
                  </div>
                )
              })
            )}
          </div>
        </div>
      </div>
    </div>
  )
}
